// CostPerCallTile.js — rolling 7-day cost ÷ call count, with prior-week trend arrow.
// Pulls a 14-day cost series + 14-day call series and slices each into two halves.

import React from 'react';
import { Card, CardContent, Typography, Box, IconButton, Tooltip as MuiTooltip } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import RemoveIcon from '@mui/icons-material/Remove';
import {
  useLLMDailySystemSeries,
  useCallVolumeSeries,
  toCSV,
  downloadCSV,
} from '../../../hooks/useAdminMetrics';

const formatPerCall = (n) => (Number.isFinite(n) ? `$${n.toFixed(4)}` : '—');

const CostPerCallTile = () => {
  const { series: costSeries, loading: costLoading, error: costError } = useLLMDailySystemSeries(14);
  const { series: callSeries, loading: callLoading, error: callError } = useCallVolumeSeries(14);

  const loading = costLoading || callLoading;
  const error = costError || callError;

  // Sum cost over a fixed-window slice of the 14-day cost series. The system
  // series may have gaps (no data on a day), so we just sum what's there.
  const sumCost = (slice) => slice.reduce((s, r) => s + (r.cost_usd || 0), 0);
  const sumCalls = (slice) => slice.reduce((s, r) => s + (r.count || 0), 0);

  // Cost series is keyed by date and only contains days with data; align by
  // taking the last 7 entries vs the prior 7. CallVolume already pads to the
  // full window, so its slicing is exact day-by-day.
  const costPrior7 = sumCost(costSeries.slice(-14, -7));
  const costLast7 = sumCost(costSeries.slice(-7));
  const callsPrior7 = sumCalls(callSeries.slice(-14, -7));
  const callsLast7 = sumCalls(callSeries.slice(-7));

  const current = callsLast7 > 0 ? costLast7 / callsLast7 : NaN;
  const prior = callsPrior7 > 0 ? costPrior7 / callsPrior7 : NaN;

  const trendDir = !Number.isFinite(current) || !Number.isFinite(prior)
    ? 'flat'
    : current > prior * 1.02 ? 'up'
    : current < prior * 0.98 ? 'down'
    : 'flat';

  const TrendIcon = trendDir === 'up' ? ArrowUpwardIcon
    : trendDir === 'down' ? ArrowDownwardIcon
    : RemoveIcon;
  const trendColor = trendDir === 'up' ? 'error.main'
    : trendDir === 'down' ? 'success.main'
    : 'text.secondary';

  const handleCSV = () => {
    const rows = [
      { window: 'last_7d', cost_usd: costLast7, calls: callsLast7, cost_per_call: current },
      { window: 'prior_7d', cost_usd: costPrior7, calls: callsPrior7, cost_per_call: prior },
    ];
    downloadCSV(
      'cost-per-call-14d.csv',
      toCSV(rows, ['window', 'cost_usd', 'calls', 'cost_per_call']),
    );
  };

  if (error) {
    return (
      <Card><CardContent>
        <Typography color="error">Cost-per-call unavailable: {error.message}</Typography>
      </CardContent></Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" gutterBottom>Cost per Call (7d)</Typography>
          <MuiTooltip title="Download CSV (current + prior 7d)">
            <span>
              <IconButton size="small" onClick={handleCSV} disabled={loading}>
                <DownloadIcon fontSize="small" />
              </IconButton>
            </span>
          </MuiTooltip>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
          <Typography variant="h3">{loading ? '…' : formatPerCall(current)}</Typography>
          {!loading && (
            <Box sx={{ display: 'flex', alignItems: 'center', color: trendColor }}>
              <TrendIcon fontSize="small" />
              <Typography variant="caption" sx={{ ml: 0.5 }}>
                vs {formatPerCall(prior)} prior 7d
              </Typography>
            </Box>
          )}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          {loading ? '' : `$${costLast7.toFixed(2)} cost / ${callsLast7.toLocaleString()} calls`}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
          Total LLM cost divided by number of phone calls, rolling 7 days.
        </Typography>
      </CardContent>
    </Card>
  );
};

export default CostPerCallTile;
